<script setup lang="ts">
import { computed, nextTick, ref } from "vue";
import type { ComponentExposed } from "vue-component-type-helpers";

import Toggle from "~/components/dumb/Toggle.vue";
import UpgradeConfirmationDialog from "~/components/dumb/UpgradeConfirmationDialog.vue";
import SettingsUpgradeCtas from "~/components/settings/SettingsUpgradeCtas.vue";
import { ButtonSize, Entitlement, PropertyKind, type StatusKind, Subscription } from "~/shared/enums";
import type { PropertyAnyStatus, PropertyConfig, Status } from "~/shared/types";
import { useDataStore, useTenantStore } from "~/stores";
import { makeStringComparator, STATUS_SORT_ORDER } from "~/utils/comparator";
import { getOrdersBetween } from "~/utils/orderManager";

import SettingsTable from "./SettingsTable.vue";
import StatusTableItem from "./StatusTableItem.vue";

const props = defineProps<{
  property: PropertyAnyStatus;
  propertyConfig: PropertyConfig<PropertyKind.DEFAULT_STATUS> | PropertyConfig<PropertyKind.STATUS>;
  scrollContainer: HTMLElement | null;
}>();

const dataStore = useDataStore();
const tenantStore = useTenantStore();

const confirmationDialog = ref<InstanceType<typeof UpgradeConfirmationDialog> | null>(null);
const upgradeRequired = computed(() => !tenantStore.getEntitlementValue(Entitlement.STATUSES));

type SettingsTableInstance = ComponentExposed<typeof SettingsTable<Status>>;

const categoriesRef = new Map<StatusKind, SettingsTableInstance>();
const assignCategoriesRef = (category: StatusKind, elem: SettingsTableInstance | null) => {
  if (!elem) {
    return;
  }
  categoriesRef.set(category, elem);
};

const checkUpgradeAndBlock = (): boolean => {
  if (upgradeRequired.value) {
    confirmationDialog.value?.openModal();
    return true;
  }
  return false;
};

const statusItemsByKind = computed(
  () =>
    new Map<StatusKind, Status[]>(
      STATUS_SORT_ORDER.map((category) => {
        const statuses = dataStore
          .getStatusesByKinds([category], props.property)
          .sort(makeStringComparator((e) => e.order))
          .map((status) => ({
            ...status,
            undraggable: status.locked,
          }));
        return [category, statuses];
      })
    )
);

const createStatus = async (kind: StatusKind) => {
  if (checkUpgradeAndBlock()) {
    return;
  }

  const statuses = statusItemsByKind.value.get(kind);
  if (!statuses) {
    return;
  }

  const newOrder = getOrdersBetween(statuses[statuses.length - 1]?.order, undefined)[0];
  await dataStore.createStatus(kind, props.property, newOrder);

  nextTick(() => {
    const itemRefs = categoriesRef.get(kind)?.itemRefs ?? [];
    itemRefs[itemRefs.length - 1]?.startEditing();
  });
};

const onDelete = (status: Status) => {
  if (checkUpgradeAndBlock()) {
    return;
  }

  dataStore.deleteStatus(status, props.property);
};

const updateStatus = (update: {
  duid: string;
  title?: string;
  colorHex?: string;
  kind?: StatusKind;
  order?: string;
}) => {
  if (checkUpgradeAndBlock()) {
    return;
  }

  dataStore.updateStatus(update);
};

const moveStatus = (category: string, status: Status) => {
  if (upgradeRequired.value) {
    categoriesRef.forEach((cat) => cat.reset());
    confirmationDialog.value?.openModal();
    return;
  }

  const statusKind = statusItemsByKind.value.get(category as StatusKind);
  if (!statusKind) {
    return;
  }

  dataStore.updateStatus({
    duid: status.duid,
    order: status.order,
    kind: category as StatusKind,
  });
};

const updateNameShown = (isNameShown: boolean) => {
  dataStore.updateProperty({
    duid: props.property.duid,
    adtl: { ...props.property.adtl, isNameShown },
  });
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <Toggle
      class="w-full"
      :size="ButtonSize.SMALL"
      :value="Boolean(props.property.adtl.isNameShown)"
      label="Display name"
      text-sm
      text-lt
      description="Always show the name of the status rather than just the icon"
      @update="(e) => updateNameShown(e)" />

    <UpgradeConfirmationDialog
      v-if="upgradeRequired"
      ref="confirmationDialog"
      feature-action="use custom statuses"
      :subscription="Subscription.PREMIUM"
      no-button />

    <div class="flex items-center gap-2 pt-4">
      <span class="text-sm text-lt select-none">Workflow</span>
      <SettingsUpgradeCtas v-if="upgradeRequired" :subscription="Subscription.PREMIUM" upgrade-only />
    </div>

    <div class="space-y-4">
      <div v-for="[category, items] in statusItemsByKind" :key="category">
        <SettingsTable
          :ref="(elem) => assignCategoriesRef(category, elem as SettingsTableInstance | null)"
          :title="category"
          :items="items"
          :item-component="StatusTableItem"
          :get-component-props="
            (status, index) => ({
              status,
              isFirst: index === 0,
              onEdit: updateStatus,
              onDelete: () => onDelete(status),
              onChangePrevented: () => confirmationDialog?.openModal(),
            })
          "
          :on-create="() => createStatus(category)"
          :on-change="moveStatus"
          drag-group="statuses"
          :drag-category="category" />
      </div>
    </div>
  </div>
</template>
